/* ==========================================================================
   AGENCY — behaviour
   Small, dependency-free. Each feature binds by data attribute and re-binds
   when the theme editor reloads a section (shopify:section:load).
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Header: scrolled state + mobile drawer ---------- */

  function initHeader(scope) {
    var header = (scope || document).querySelector('[data-ag-header]');
    if (!header || header.__agBound) return;
    header.__agBound = true;

    var burger = header.querySelector('[data-ag-burger]');

    function setOpen(open) {
      header.classList.toggle('is-open', open);
      if (burger) {
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      }
    }

    if (burger) {
      burger.addEventListener('click', function () {
        setOpen(!header.classList.contains('is-open'));
      });
    }

    header.addEventListener('click', function (e) {
      var link = e.target.closest('[data-ag-drawer] a');
      if (link) setOpen(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') setOpen(false);
    });

    document.addEventListener('click', function (e) {
      if (header.classList.contains('is-open') && !header.contains(e.target)) setOpen(false);
    });

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        header.classList.toggle('is-scrolled', window.scrollY > 30);
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Reveal on scroll ---------- */

  var revealObserver = null;

  function initReveal(scope) {
    var items = (scope || document).querySelectorAll('[data-ag-reveal]:not(.is-in)');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }

    items.forEach(function (el) { revealObserver.observe(el); });
  }

  /* ---------- Work carousel (3-up coverflow) ---------- */

  function initCarousel(root) {
    if (root.__agBound) return;
    root.__agBound = true;

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-ag-card]'));
    var n = cards.length;
    if (!n) return;

    var index = 0;
    var timer = null;
    var autoplay = root.getAttribute('data-autoplay') === 'true' && !reduceMotion;
    var delay = parseInt(root.getAttribute('data-delay'), 10) || 4500;

    function mod(i) { return ((i % n) + n) % n; }

    function render() {
      cards.forEach(function (card, i) {
        card.classList.remove('is-center', 'is-left', 'is-right');
        card.setAttribute('aria-hidden', 'true');
        card.querySelectorAll('a, button').forEach(function (el) { el.setAttribute('tabindex', '-1'); });

        if (i === index) {
          card.classList.add('is-center');
          card.setAttribute('aria-hidden', 'false');
          card.querySelectorAll('a, button').forEach(function (el) { el.removeAttribute('tabindex'); });
        } else if (n > 1 && i === mod(index - 1)) {
          card.classList.add('is-left');
        } else if (n > 2 && i === mod(index + 1)) {
          card.classList.add('is-right');
        } else if (n === 2 && i === mod(index + 1)) {
          card.classList.add('is-right');
        }
      });
    }

    function go(step) {
      index = mod(index + step);
      render();
      restart();
    }

    function restart() {
      if (!autoplay) return;
      window.clearInterval(timer);
      timer = window.setInterval(function () { index = mod(index + 1); render(); }, delay);
    }

    var prev = root.querySelector('[data-ag-prev]');
    var next = root.querySelector('[data-ag-next]');
    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });

    cards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (card.classList.contains('is-left')) { e.preventDefault(); go(-1); }
        else if (card.classList.contains('is-right')) { e.preventDefault(); go(1); }
      });
    });

    // Swipe on touch screens.
    var startX = null;
    root.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', function (e) {
      if (startX === null) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
      startX = null;
    });

    root.addEventListener('mouseenter', function () { window.clearInterval(timer); });
    root.addEventListener('mouseleave', restart);

    render();
    restart();
  }

  /* ---------- Process timeline progress ---------- */

  function initProcess(root) {
    if (root.__agBound) return;
    root.__agBound = true;

    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-ag-step]'));
    var list = root.querySelector('[data-ag-steps]');
    if (!list) return;

    var ticking = false;
    function update() {
      ticking = false;
      var rect = list.getBoundingClientRect();
      var vh = window.innerHeight;
      var anchor = vh * 0.55;
      var progress = (anchor - rect.top) / Math.max(1, rect.height - 100);
      progress = Math.max(0, Math.min(1, progress));
      list.style.setProperty('--ag-progress', progress.toFixed(4));

      steps.forEach(function (step) {
        var r = step.getBoundingClientRect();
        step.classList.toggle('is-active', r.top + 18 < anchor);
      });
    }
    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  /* ---------- FAQ accordion ---------- */

  function initFaq(root) {
    if (root.__agBound) return;
    root.__agBound = true;
    var single = root.getAttribute('data-single') === 'true';

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-ag-faq-q]');
      if (!btn) return;
      var item = btn.closest('[data-ag-faq-item]');
      var open = !item.classList.contains('is-open');

      if (single && open) {
        root.querySelectorAll('[data-ag-faq-item].is-open').forEach(function (other) {
          other.classList.remove('is-open');
          var b = other.querySelector('[data-ag-faq-q]');
          if (b) b.setAttribute('aria-expanded', 'false');
        });
      }

      item.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ---------- Booking iframe spinner ---------- */

  function initBooking(root) {
    if (root.__agBound) return;
    root.__agBound = true;
    var frame = root.querySelector('iframe');
    if (!frame) return;
    frame.addEventListener('load', function () { root.classList.add('is-loaded'); });
    window.setTimeout(function () { root.classList.add('is-loaded'); }, 6000);
  }

  /* ---------- Scroll to top ---------- */

  function initTop() {
    var btn = document.querySelector('[data-ag-top]');
    if (!btn || btn.__agBound) return;
    btn.__agBound = true;
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
    window.addEventListener('scroll', function () {
      btn.classList.toggle('is-visible', window.scrollY > 700);
    }, { passive: true });
  }

  /* ---------- Boot ---------- */

  function initAll(scope) {
    scope = scope || document;
    initHeader(scope);
    initReveal(scope);
    scope.querySelectorAll('[data-ag-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-ag-process]').forEach(initProcess);
    scope.querySelectorAll('[data-ag-faq]').forEach(initFaq);
    scope.querySelectorAll('[data-ag-booking]').forEach(initBooking);
    initTop();
  }

  document.documentElement.classList.add('ag-js');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
